import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Popup from "reactjs-popup";

import { useMainStore } from "@/contexts/Store";

import trashIcon from "../../../images/table-image/icon/delete-icon.svg";
import { Icon } from "../../Elements";
import Checkbox from "../shared/Checkbox";
import ConfirmationDialog from "../shared/ConfirmationDialog";

const CM_MODULE_IDENTIFIER = "control_mapping";
const COLUMNS = ["Control Category", "Type", "Control Title", "Description"];

type Props = {
  moduleWorkspaceID: number;
  libraryType: string;
};

function Library({ moduleWorkspaceID, libraryType }: Props) {
  // Import MobX stores
  const mainStore = useMainStore();

  // State
  const [openedDeletePopupID, setOpenedDeletePopupID] = useState(null);
  const [search, setSearch] = useState("");

  // Variables
  const history = useHistory();
  const { isIW: isInternalWorkspace, workspaceID } = mainStore.context;
  const { canDeleteRecords, hasModuleWriteAccess } = mainStore.userPermissions;
  const { list: moduleWorkspacesList } = mainStore.moduleWorkspaces;
  const { libraryRecords: rows, data } = mainStore.controlMappings;
  const moduleWorkspace = moduleWorkspacesList?.find(
    (item) => item.themis_module.identifier === CM_MODULE_IDENTIFIER,
  );
  const libraryIsEnabled =
    isInternalWorkspace || moduleWorkspace?.library_enabled;

  const filteredRows = rows.filter(
    (item) =>
      search === "" ||
      item.control_category?.toLowerCase().includes(search.toLowerCase()) ||
      item.control_mapping_type?.toLowerCase().includes(search.toLowerCase()) ||
      item.title?.toLowerCase().includes(search.toLowerCase()) ||
      item.description?.toLowerCase().includes(search.toLowerCase()),
  );

  // Effects
  useEffect(() => {
    if (!moduleWorkspaceID) {
      return;
    }

    mainStore.controlMappings.setData({
      ...data,
      module_workspace_id: moduleWorkspaceID,
    });
    mainStore.controlMappings.fetchLibrary(moduleWorkspaceID, libraryType);
  }, [moduleWorkspaceID]);

  // Functions
  // @ts-expect-error TS(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
  function handleMainCheckboxChange(e) {
    const { checked } = e.target;

    mainStore.controlMappings.setLibraryRecords(
      rows.map((item) => {
        if (!filteredRows.includes(item)) {
          return item;
        }
        return { ...item, checked };
      }),
    );
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'e' implicitly has an 'any' type.
  function handleRegularCheckboxChange(e, id) {
    const { checked } = e.target;

    mainStore.controlMappings.setLibraryRecords(
      rows.map((item) => (item.id === id ? { ...item, checked } : item)),
    );
  }

  async function createNewLibraryItem() {
    const newItem = await mainStore.controlMappings.createLibraryItem();
    history.push(
      `/workspaces/${workspaceID}/modules/control-mapping/templates/${newItem.id}`,
    );
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'id' implicitly has an 'any' type.
  function handleDelete(id) {
    mainStore.controlMappings.deleteLibraryItem(id);
    setOpenedDeletePopupID(null);
  }

  // elements
  const renderHeading = (
    <div className="rr-library-heading" data-testid="cm-library-heading">
      <h4>
        {libraryType == "company_generated" ? "Company" : "Themis"} Library
      </h4>
      <div>Select the preferred Control by checking the item.</div>
    </div>
  );

  const renderSearch = (
    <div className="controls-search-container">
      <input
        type="text"
        placeholder="Search Control"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Icon name="search" color="generalDark" size="de" />
    </div>
  );

  const renderAddNewButton = (
    <div className="create-new-record risk-register library">
      <div className="plus-sign" onClick={createNewLibraryItem}>
        <Icon name="plus" color="generalWhite" size="de" />
      </div>
      <button
        type="button"
        onClick={createNewLibraryItem}
        data-testid="cm-library-add-new"
      >
        Add New Library Item +
      </button>
    </div>
  );

  // @ts-expect-error TS(7006) FIXME: Parameter 'column' implicitly has an 'any' type.
  const renderColumn = (column) => (
    <div
      key={column}
      className="rr-library-table-column"
      data-testid="cm-library-column"
    >
      <Icon name="book1" color="generalDarkGray" size="de" />
      {column}
    </div>
  );

  // @ts-expect-error TS(7006) FIXME: Parameter 'row' implicitly has an 'any' type.
  const renderRow = (row) => (
    <div className="rr-library-table-row-container">
      <div
        key={row.id}
        className="rr-library-table-row"
        data-testid="rr-library-row"
      >
        <Checkbox
          checked={row.checked}
          onChange={(e) => handleRegularCheckboxChange(e, row.id)}
          data-testid="rr-library-regular-checkbox"
          disabled={!hasModuleWriteAccess}
          padding
        />
        <div
          className="rr-library-table-cell option risk-type"
          data-testid="rr-library-cell-control-category"
        >
          <span>{row.control_category || "-No Control Category-"}</span>
        </div>
        <div
          className="rr-library-table-cell option"
          data-testid="rr-library-cell-control-mapping-type"
        >
          <span>{row.control_mapping_type || "-No Type-"}</span>
        </div>
        <div
          className="rr-library-table-cell"
          data-testid="rr-library-cell-title"
        >
          <span>{row.title || "-No Title-"}</span>
        </div>
        <div
          className="rr-library-table-cell"
          data-testid="rr-library-cell-description"
        >
          <span>{row.description || "-No Description-"}</span>
        </div>
      </div>
      {libraryType == "company_generated" && hasModuleWriteAccess && (
        <div
          className="rr-library-table-row-actions"
          data-testid="rr-library-table-row-actions"
        >
          <Link
            to={`/workspaces/${workspaceID}/modules/control-mapping/templates/${row.id}`}
          >
            <Icon name="edit" color="generalDark" size="de" />
          </Link>
          <Popup
            position="bottom right"
            open={openedDeletePopupID ? openedDeletePopupID === row.id : false}
            trigger={() => (
              <button
                type="button"
                data-testid="cm-library-table-row-actions-delete-trigger"
                disabled={!canDeleteRecords}
              >
                <img src={trashIcon} alt="trash-icon" />
              </button>
            )}
            disabled={!canDeleteRecords || !isInternalWorkspace}
            onOpen={() => setOpenedDeletePopupID(row.id)}
            onClose={() => setOpenedDeletePopupID(null)}
          >
            <div
              className="table-dropdown success-dropdown"
              data-testid="rr-library-table-row-actions-delete-popup"
            >
              <ConfirmationDialog
                heading="Delete Library Item"
                content="Are you sure you want to delete this library item? Actions are not reversible."
                handleConfirm={() => handleDelete(row.id)}
                handleReject={() => setOpenedDeletePopupID(null)}
              />
            </div>
          </Popup>
        </div>
      )}
    </div>
  );

  return (
    <div className="rr-library-wrapper">
      <div className="rr-library-container" data-testid="rr-library-container">
        {!libraryIsEnabled && (
          <div>
            Library is disabled for this workspace. Contact Themis to enable it.
          </div>
        )}
        {libraryIsEnabled && (
          <>
            {renderHeading}
            {libraryType == "themis_generated" && (
              <div className="library-explainer">
                The information and materials available through the Themis
                platform are for informational purposes only and do not
                constitute legal advice. As legal advice must be tailored to the
                specific circumstances of each case, nothing provided on this
                site should be used as a substitute for advice of competent
                counsel.
              </div>
            )}
            {renderSearch}
            <div
              className="rr-library-table-container"
              data-testid="rr-library-table"
            >
              <div className="rr-library-table-columns">
                <Checkbox
                  checked={filteredRows.every((item) => item.checked)}
                  onChange={handleMainCheckboxChange}
                  data-testid="rr-library-main-checkbox"
                  disabled={!hasModuleWriteAccess}
                />
                {COLUMNS.map(renderColumn)}
              </div>
              {isInternalWorkspace &&
                libraryType == "company_generated" &&
                hasModuleWriteAccess &&
                renderAddNewButton}
              <div className="rr-library-table-rows">
                {filteredRows.map(renderRow)}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default observer(Library);
